'use client';

import { c as _c } from "react/compiler-runtime";
import { useIntervalEffect } from '@react-hookz/web';
import { MinusIcon, SquareIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { sdk } from '@/app/sdk';
import { cn } from '@/lib/utils';

/** Window control button component */
interface WindowControlButtonProps {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'minimize' | 'maximize' | 'close';
}
const WindowControlButton = t0 => {
  const $ = _c(18);
  const {
    onClick,
    children,
    className,
    disabled: t1,
    variant
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const [isHovered, setIsHovered] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      minimize: "hover:bg-gray-500",
      maximize: "hover:bg-gray-500",
      close: "w-[50px] -mr-2 hover:bg-[#E81123]"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const variantStyles = t2;
  const t3 = variant && variantStyles[variant];
  const t4 = disabled && "opacity-50 cursor-not-allowed";
  let t5;
  if ($[1] !== className || $[2] !== t3 || $[3] !== t4) {
    t5 = cn("flex items-center justify-center w-[46px] h-[var(--titlebar-height)] transition-colors duration-150 titlebarControls", t3, t4, className);
    $[1] = className;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = !disabled ? onClick : undefined;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsHovered(true);
    t8 = () => setIsHovered(false);
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  const t9 = variant === "minimize" ? "Minimize" : variant === "maximize" ? "Maximize" : variant === "close" ? "Close" : "Window control";
  const t10 = isHovered ? "text-white" : "text-foreground";
  let t11;
  if ($[7] !== t10) {
    t11 = cn("transition-colors duration-150", t10);
    $[7] = t10;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== children || $[10] !== t11) {
    t12 = <div className={t11}>{children}</div>;
    $[9] = children;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== disabled || $[13] !== t12 || $[14] !== t5 || $[15] !== t6 || $[16] !== t9) {
    t13 = <button className={t5} onClick={t6} disabled={disabled} onMouseEnter={t7} onMouseLeave={t8} aria-label={t9}>{t12}</button>;
    $[12] = disabled;
    $[13] = t12;
    $[14] = t5;
    $[15] = t6;
    $[16] = t9;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  return t13;
};
WindowControlButton.displayName = 'WindowControlButton';

/** Window controls component with minimize, maximize/restore, and close buttons */
export const WindowControls = () => {
  const $ = _c(12);
  const [isMaximized, setIsMaximized] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!sdk?.window?.isMaximized) {
        return;
      }
      const checkWindowState = async () => {
        ;
        try {
          const maximized = await sdk.window.isMaximized();
          setIsMaximized(maximized);
        } catch (t1) {
          const error = t1;
          console.warn("\uD83D\uDEA8 Failed to get window maximized state:", error);
        }
      };
      checkWindowState();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useIntervalEffect(t0, 500);
  const handleMinimize = _temp;
  let t1;
  if ($[1] !== isMaximized) {
    t1 = () => {
      if (!sdk?.window) {
        return;
      }
      console.log(isMaximized ? "Unmaximize window" : "Maximize window");
      if (isMaximized) {
        sdk.window.unmaximize();
        setIsMaximized(false);
      } else {
        sdk.window.maximize();
        setIsMaximized(true);
      }
    };
    $[1] = isMaximized;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleMaximize = t1;
  const handleClose = _temp2;
  if (!sdk?.window) {
    return null;
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <WindowControlButton onClick={handleMinimize} variant="minimize"><MinusIcon className="size-4" /></WindowControlButton>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isMaximized) {
    t3 = isMaximized ? <div className="relative size-3"><SquareIcon className="size-2.5 absolute top-0 left-0.5" /><SquareIcon className="size-2.5 absolute top-0.5 left-0" /></div> : <SquareIcon className="size-3" />;
    $[4] = isMaximized;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== handleMaximize || $[7] !== t3) {
    t4 = <WindowControlButton onClick={handleMaximize} variant="maximize">{t3}</WindowControlButton>;
    $[6] = handleMaximize;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <WindowControlButton onClick={handleClose} variant="close"><XIcon className="w-4.5 h-4.5" /></WindowControlButton>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <div className="flex items-center h-full ml-auto titlebarControls">{t2}{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
WindowControls.displayName = 'WindowControls';
function _temp() {
  if (!sdk?.window?.minimize) {
    return;
  }
  console.log("Minimize window");
  sdk.window.minimize();
}
function _temp2() {
  if (!sdk?.window?.close) {
    return;
  }
  console.log("Close window");
  sdk.window.close();
}